<template>
  <div class="task-list">
    <div class="ticket-header">
      <b-btn v-if="!external"
             v-b-tooltip.hover.bottom
             title="Toggle details"
             @click="showMessage = !showMessage"
             variant="outline-info"
             :class="messageToggleClass"
             class="toggle-btn">
        <span class="oi oi-chevron-top"></span>
      </b-btn>
      <h5 class="ticket-title-bar">
        <router-link v-if="parent !== 'message' && ticketObject.message_id" :to="'/messages/' + ticketObject.message_id" class="message-link">
          <span :id="ticketObject.id">#{{ ticketObject.id }} - {{ ticketObject.name }}</span>
        </router-link>
        <span v-else :id="ticketObject.id">#{{ ticketObject.id }} - {{ ticketObject.name }}</span>
      </h5>
      <template v-if="!external">
        <b-dropdown id="ddown-buttons" class="m-2">
          <b-dropdown-item @click="showNewTaskModal(ticketObject.id)">Add Task</b-dropdown-item>
          <b-dropdown-item @click="showTicketModal(ticketObject)">Edit Ticket</b-dropdown-item>
        </b-dropdown>
        <div v-if="tasksComplete" class="ticket-complete">
          <b-btn @click="requestReview" :variant="reviewRequestVariant">
            <span>
              {{reviewRequestText}}
              <fa v-if="ticketObject.review_request == 'approved'" icon="check-circle"/>
            </span>
          </b-btn>
        </div>
        <div v-show="ticketComplete || ((ticketObject.review_request == 'approved' || ticketObject.review_request == 'unrequested') && tasksComplete)" class="ticket-complete">
          <b-check class="ticket-complete-check" v-model="ticketObject.complete">
          </b-check>
          <p>Complete</p>
        </div>
        <span class="task-edit-btn" v-b-tooltip.hover.bottom title="Add Task" @click="showNewTaskModal(ticketObject.id)">
          <fa icon="plus-circle"/>
        </span>
        <span v-if="selectedTasks.length === 0" class="task-edit-btn" v-b-tooltip.hover.bottom title="Select All" @click="selectAll">
          <fa icon="hand-pointer"/>
        </span>
        <span v-else class="task-edit-btn" v-b-tooltip.hover.bottom title="Deselect All" @click="selectAll">
          <fa icon="hand-pointer"/>
        </span>
        <span class="task-edit-btn" v-b-tooltip.hover.bottom title="Edit Tasks" v-if="selectedTasks.length > 0" @click="showTaskActionsModal">
          <fa icon="pen"/>
        </span>
        <span class="task-edit-btn" v-b-tooltip.hover.bottom title="Clone Ticket" @click="cloneTicket(ticketObject)">
          <fa icon="clone"/>
        </span>
      </template>
      <div class="timestamp-task-button">
        <span v-b-tooltip.hover.left :title="detailedDate(ticketObject.created_at)" class="ticket-timestamp">
          Created {{prettyDate(ticketObject.created_at)}}
        </span>
        <b-btn v-if="parent !== 'message' && showTaskToggle" v-b-tooltip.hover.bottom title="Toggle tasks" v-b-toggle="tasksToggle" variant="outline-secondary" class="task-toggle-button toggle-btn">
          <span class="oi oi-chevron-top"></span>
        </b-btn>
      </div>
      <b-progress :value="totalTasksComplete" :max="totalTasks" height="5px"></b-progress>

      <template v-if="!external">
        <b-card id="description-card" v-show="showMessage" @dblclick="descriptionEdit = !descriptionEdit" title="Notes:">
          <div class="ticket-description" v-show="!descriptionEdit" v-html="ticketObject.description"></div>
          <b-form-textarea v-show="descriptionEdit" v-model="ticketObject.description" @input="updateDescription(ticketObject)" />
          <br>
          <section>
            <TicketAudit v-for="audit in ticketObject.request_audits"
                         :key="audit.created_at"
                         :audit="audit"/>
          </section>
        </b-card>
        <b-tooltip target="description-card" title="double click to edit" placement="bottom"></b-tooltip>
      </template>

      <template v-if="parent !== 'message'">
        <span v-if="ticketObject.message_id && showMessage">
          <Message :name="'Message'" :id="ticketObject.message_id" :parent="'ticket'"/>
        </span>
      </template>
    </div>

    <b-collapse :id="'collapse' + ticketObject.id + 'tasks'" v-model="showCollapse">
      <draggable v-if="!external" v-model="tasks" @update="updateTasksOrder">
        <transition-group>
          <TaskItem v-for="task in tasks" :task="task" :key="task.id" v-on:taskUpdated="taskSubmitSuccessful" :selectedTasks="selectedTasks" @taskSelected="handleSelect" :external="external" />
        </transition-group>
      </draggable>
      <template v-if="external">
        <TaskItem v-for="task in tasks" :task="task" :key="task.id" v-on:taskUpdated="taskSubmitSuccessful" :selectedTasks="selectedTasks" @taskSelected="handleSelect" :external="external"/>
      </template>
    </b-collapse>
    <TaskActions v-if="selectedTasks.length > 0" :selectedTasks="selectedTasks" :ticket="ticket"/>
  </div>
</template>

<script>
import _ from 'lodash'
import draggable from 'vuedraggable'
import { collectionsMixins } from '@/mixins/collectionsMixins'
import { helpersMixins } from '@/mixins/helpersMixins'
import { mapGetters } from 'vuex'
import Message from './Message'
import TaskItem from './TaskItem'
import SingleUserSelector from './selectors/users/SingleUserSelector.vue'
import TicketAudit from './TicketAudit'
import TaskActions from './TaskActions'
export default {
  name: 'Ticket',
  mixins: [collectionsMixins, helpersMixins],
  props: {
    ticket: Object,
    user: Object,
    parent: String,
    default_board_id: Number,
    external: Boolean
  },
  components: {
    Message,
    SingleUserSelector,
    draggable,
    TaskItem,
    TicketAudit,
    TaskActions
  },
  mounted () {
    this.$root.$on('reloadTasks', (ticketId) => {
      if (this.ticketObject.id === ticketId) {
        this.selectedTasks = []
        this.getTasks()
      }
    })
    this.$root.$on('searchPerformedForTasks', (ids) => {
      this.getTasks(ids)
    })
    this.$root.$on('ticketWasUpdated', (ticket) => {
      if (this.ticketObject.id === ticket.id) {
        this.ticketObject = ticket
        this.getTasks()
      }
    })
  },
  data () {
    return {
      ticketObject: {
        message_id: null,
        id: null,
        showTaskToggle: null,
        description: 'Enter description here'
      },
      tasks: [
      ],
      selectedTasks: [
      ],
      showMessage: false,
      descriptionEdit: false,
      showCollapse: true
    }
  },
  created () {
    if (this.ticket) {
      this.ticketObject = this.ticket
      this.getTasks()
      this.showTaskToggle = true
      this.$root.$on('toggleTicketCollapse', (value) => {
        this.setTicketToggle(value)
      })
    } else {
      this.getTicket()
      this.showTaskToggle = false
    }
  },
  methods: {

    cloneTicket (ticket) {
      ticket.task_board_id = this.default_board_id
      this.$root.$emit('ticketClone', ticket)
    },

    setTicketToggle (value) {
      this.showCollapse = value
    },
    updateTasksOrder () {
      this.tasks.forEach((task, index) => {
        let taskObject = task
        taskObject.order = index
        this.$http.put('/tasks/' + task.id, { task: taskObject })
          .then()
          .catch(e => { console.log(e) })
      })
    },

    showTicketModal (ticket) {
      ticket.task_board_id = this.default_board_id
      this.$root.$emit('ticketEdit', ticket)
    },

    showNewTaskModal (ticketId) {
      var task = { ticket_id: ticketId }
      this.$root.$emit('taskEdit', task)
    },

    showTaskActionsModal () {
      this.$root.$emit('taskActions', {tasks: this.selectedTasks, ticket: this.ticket})
    },

    loggingThis () {
      if (event.target.checked) {
        event.target.parentNode.parentNode.parentNode.classList.add('half-opacity')
      } else {
        event.target.parentNode.parentNode.parentNode.classList.remove('half-opacity')
      }
    },

    updateDescription: _.debounce(function (ticket) {
      var params = { description: ticket.description }
      this.updateTicket(params)
    }, 2000),

    updateTicket (params) {
      this.$http.put('/tickets/' + this.ticketObject.id, params)
        .then(response => this.ticketSubmitSuccessful(response))
        .catch(e => { console.log(e) })
    },

    getTasks (ids) {
      var params
      if (this.user) {
        params = { ticket_id: this.ticketObject.id, user_id: this.user.id }
      } else {
        params = { ticket_id: this.ticketObject.id }
      }
      if (ids) {
        params['ids'] = ids
      }
      if (this.external) {
        params.message_id = this.ticket.message_id
        params.external_token = this.$route.params.token
      }
      var vm = this
      this.$http
        .get('/tasks', { params: params })
        .then(function (response) {
          vm.tasks = response.data
        })
        .catch(e => { console.log(e.response) })
    },

    taskSubmitSuccessful (response) {
      var taskObject = response.data
      var taskIndex = this.tasks.findIndex(task => task.id === taskObject.id)
      this.tasks.splice(taskIndex, 1, taskObject)
    },

    ticketSubmitSuccessful (response) {
      this.ticketObject = response.data
      this.descriptionEdit = false
    },

    getTicket () {
      var vm = this
      if (this.ticketObject.id) {
        return false
      } else {
        this.$http.get('/tickets/' + vm.$route.params.id)
          .then(function (response) {
            vm.ticketObject = response.data
            vm.getTasks()
          })
      }
    },
    requestReview () {
      if (this.ticketObject.review_request !== 'requested') {
        this.updateTicket({review_request: 'requested'})
      } else if (this.currentUser.roles.includes('reviewer')) {
        this.showTicketModal(this.ticketObject)
      }
    },
    selectAll () {
      if (this.selectedTasks.length > 0) {
        this.selectedTasks = []
      } else {
        for (var i = 0; i < this.tasks.length; i++) {
          this.selectedTasks.push(this.tasks[i])
        }
      }
    },
    handleSelect (item) {
      if (!(this.selectedTasks.includes(item))) {
        this.selectedTasks.push(item)
      } else {
        let index = this.selectedTasks.indexOf(item)
        if (index > -1) {
          this.selectedTasks.splice(index, 1)
        }
      }
    }
  },
  computed: {
    tasksToggle () {
      return 'collapse' + this.ticketObject.id + 'tasks'
    },
    ...mapGetters(['currentUser']),
    tasksComplete () {
      if (this.totalTasksComplete === this.tasks.length) {
        return true
      } else {
        return false
      }
    },
    ticketComplete () {
      if (this.ticketObject.complete === true) {
        return true
      } else {
        return false
      }
    },
    totalTasks () {
      return this.tasks.length
    },
    totalTasksComplete () {
      return this.tasks.filter(task => task.status === 'complete').length
    },
    messageToggleClass () {
      if (!this.showMessage) {
        return 'collapsed'
      } else {
        return ''
      }
    },
    reviewRequestText () {
      if (this.ticketObject.review_request === 'requested') {
        return 'Review requested'
      } else if (this.ticketObject.review_request === 'approved') {
        return 'Approved'
      } else if (this.ticketObject.review_request === 'disapproved') {
        return 'Disapproved'
      } else {
        return 'Request review'
      }
    },
    reviewRequestVariant () {
      if (this.ticketObject.review_request === 'requested') {
        return 'outline-info'
      } else if (this.ticketObject.review_request === 'approved') {
        return 'outline-success'
      } else if (this.ticketObject.review_request === 'disapproved') {
        return 'outline-danger'
      } else {
        return 'outline-primary'
      }
    }
  },
  watch: {
    ticketComplete () {
      var params = { complete: this.ticketComplete }
      this.updateTicket(params)
    }
  },
  beforeDestry () {
    this.$root.$off('toggleTicketCollapse')
  }
}
</script>

<style scoped>
table {
  border-right: 1px solid #dee2e6;
  border-left: 1px solid #dee2e6;
  border-bottom: 1px solid #dee2e6;
  margin-top: 5px;
}
.custom-control.custom-checkbox.custom-control-inline {
  margin-right: 0px;
  vertical-align: middle;
  padding-right: 0px;
}
.ticket-title-bar {
  display: inline-block;
  margin-bottom: 0;
  vertical-align: middle;
}
.task-list {
  text-align: left;
  margin-bottom: 10px;
  background: #fafafa;
  border-radius: 10px;
  padding: 5px 10px;
  position: relative;
}
.ticket-header {
  position: sticky;
  top: 0px;
  z-index: 10;
  padding: 5px 0;
  background-color: inherit;
}
.collapsed .oi-chevron-top {
  transform: translateY(2px) rotateX(180deg);
}
.toggle-btn {
  border-radius: 50%;
}
span.edit-icons {
  color: #000;
  opacity: .5;
}
button.btn {
  height: 42px;
}
.timestamp-task-button {
  float: right;
}
.ticket-timestamp {
  color: #7A858E;
  font-size: 12px;
}
.btn-outline-info {
  color: #57B2CD;
  border-color: #57B2CD;
}
.btn-outline-info:hover {
  color: #FFF;
  background-color: #57B2CD;
  border-color: #57B2CD;
}
.message-link {
  color: #007BFF !important;
}
.table-notes-field {
  min-height: 20px;
}
.single-user-selector {
  width: 200px;
}
.task-edit-btn {
  cursor: pointer;
}
@media (max-width: 767px) {
  .timestamp-task-button {
    display: none;
  }
  div.ticket-header {
    top: -10px;
  }
}
</style>
<style>
.table-notes {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
}
.table-client {
  white-space: normal;
}
td {
  vertical-align: middle !important;
}
div#ddown-buttons > button {
    background: transparent;
    color: #000;
    border: none;
    padding: 0;
}
.ticket-complete {
  height: 30px;
}
.ticket-complete, .ticket-complete-check, .ticket-complete > p {
  display: inline-block;
}
.ticket-complete-check {
  margin-top: -20px;
}
.ticket-complete > p {
  margin-left: 10px;
}
.ticket-complete-check > .custom-control-label::before, .ticket-complete-check > .custom-control-label::after {
  width: 2rem;
  height: 2rem;
}
.ticket-description {
  white-space: pre-wrap;
}
.progress {
  margin-top: 5px;
}
.progress .progress-bar {
  background-color: #27B4D0;
}
.btn-outline-info {
  color: #57B2CD;
  border-color: #57B2CD;
}
.btn-outline-info:hover {
  color: #FFF;
  background-color: #57B2CD;
  border-color: #57B2CD;
}
</style>



// WEBPACK FOOTER //
// src/components/Ticket.vue